import { UPLOAD_STRINGS } from "@/lib/uploadI18n";

// Keep résumé file-format guidance explicit in every supported app language.
// Users should see the exact accepted Word format (.docx), not vague wording
// such as "modern Word". These values override the upload sanitizer copy at
// module load time without changing the sanitizer error-code contract.
const FORMAT_COPY = {
  en: {
    sanUnsupported: "Unsupported file type. Please upload a text-based PDF or Microsoft Word (.docx) file.",
    sanLegacyDoc: "My English Coach Interview Simulator accepts only Microsoft Word (.docx) and text-based PDF files. We do not process the older .doc format. Please save the file as .docx and try again.",
  },
  "pt-BR": {
    sanUnsupported: "Formato de arquivo não suportado. Envie um PDF baseado em texto ou um arquivo do Microsoft Word (.docx).",
    sanLegacyDoc: "O My English Coach Interview Simulator aceita apenas arquivos do Microsoft Word (.docx) e PDFs baseados em texto. Não processamos o formato antigo .doc. Salve o arquivo como .docx e tente novamente.",
  },
  "es-419": {
    sanUnsupported: "Formato de archivo no compatible. Suba un PDF basado en texto o un archivo de Microsoft Word (.docx).",
    sanLegacyDoc: "My English Coach Interview Simulator acepta únicamente archivos de Microsoft Word (.docx) y PDF basados en texto. No procesamos el formato antiguo .doc. Guarde el archivo como .docx e inténtelo de nuevo.",
  },
  "fr-CA": {
    sanUnsupported: "Format de fichier non pris en charge. Téléversez un PDF texte ou un fichier Microsoft Word (.docx).",
    sanLegacyDoc: "My English Coach Interview Simulator accepte uniquement les fichiers Microsoft Word (.docx) et les PDF texte. Nous ne traitons pas l’ancien format .doc. Enregistrez le fichier en .docx et réessayez.",
  },
};

// Copy for the two AI sample-role generation paths. Resume mode explicitly
// tells the user that only the reviewed post-Privacy Scan résumé text is used.
const SAMPLE_ROLE_COPY = {
  en: {
    jdSampleChoiceH: "How should AI create your practice role?",
    jdSampleFromResume: "Create a practice role based on my résumé",
    jdSampleFromResumeSub: "AI will use the résumé text you reviewed after the Privacy Scan to create a realistic role suited to your experience.",
    jdSampleSpecified: "Let me choose the type of role",
    jdSampleSpecifiedSub: "Select a professional category and role level, and optionally describe the role you want to practise for. Your résumé is not used to create this job description.",
    jdSampleResumeNotReady: "Review and approve your post-Privacy Scan résumé above before generating a role from it.",
  },
  "pt-BR": {
    jdSampleChoiceH: "Como a IA deve criar sua vaga de prática?",
    jdSampleFromResume: "Criar uma vaga de prática com base no meu currículo",
    jdSampleFromResumeSub: "A IA usará o texto do currículo que você revisou após a Verificação de Privacidade para criar uma vaga realista adequada à sua experiência.",
    jdSampleSpecified: "Quero escolher o tipo de vaga",
    jdSampleSpecifiedSub: "Selecione uma categoria profissional e o nível da vaga e, se quiser, descreva a função para a qual deseja praticar. Seu currículo não será usado para criar esta descrição de vaga.",
    jdSampleResumeNotReady: "Revise e aprove seu currículo após a Verificação de Privacidade acima antes de gerar uma vaga com base nele.",
  },
  "es-419": {
    jdSampleChoiceH: "¿Cómo debe crear la IA su puesto de práctica?",
    jdSampleFromResume: "Crear un puesto de práctica basado en mi currículum",
    jdSampleFromResumeSub: "La IA usará el texto del currículum que usted revisó después del Análisis de Privacidad para crear un puesto realista acorde con su experiencia.",
    jdSampleSpecified: "Quiero elegir el tipo de puesto",
    jdSampleSpecifiedSub: "Seleccione una categoría profesional y el nivel del puesto y, si lo desea, describa el rol para el que quiere practicar. Su currículum no se utilizará para crear esta descripción del puesto.",
    jdSampleResumeNotReady: "Revise y apruebe arriba su currículum después del Análisis de Privacidad antes de generar un puesto basado en él.",
  },
  "fr-CA": {
    jdSampleChoiceH: "Comment l’IA doit-elle créer votre poste d’entraînement?",
    jdSampleFromResume: "Créer un poste d’entraînement à partir de mon CV",
    jdSampleFromResumeSub: "L’IA utilisera le texte du CV que vous avez révisé après l’Analyse de confidentialité afin de créer un poste réaliste adapté à votre expérience.",
    jdSampleSpecified: "Je veux choisir le type de poste",
    jdSampleSpecifiedSub: "Sélectionnez une catégorie professionnelle et un niveau de poste et, si vous le souhaitez, décrivez le rôle pour lequel vous voulez vous exercer. Votre CV ne sera pas utilisé pour créer cette description de poste.",
    jdSampleResumeNotReady: "Révisez et approuvez votre CV après l’Analyse de confidentialité ci-dessus avant de générer un poste à partir de celui-ci.",
  },
};

for (const lang of Object.keys(UPLOAD_STRINGS)) {
  Object.assign(UPLOAD_STRINGS[lang], FORMAT_COPY[lang] || FORMAT_COPY.en, SAMPLE_ROLE_COPY[lang] || SAMPLE_ROLE_COPY.en);
}

const COPY = {
  en: "This interview is conducted in English, so please upload an English version of your résumé. If your résumé is currently in another language, translate and review it before uploading.",
  "pt-BR": "Esta entrevista será realizada em inglês. Envie uma versão em inglês do seu currículo. Se o seu currículo estiver em outro idioma, traduza-o e revise-o antes de enviá-lo.",
  "es-419": "Esta entrevista se realiza en inglés. Suba una versión en inglés de su currículum. Si su currículum está actualmente en otro idioma, tradúzcalo y revíselo antes de subirlo.",
  "fr-CA": "Cette entrevue se déroule en anglais. Veuillez téléverser une version anglaise de votre CV. Si votre CV est actuellement dans une autre langue, traduisez-le et révisez-le avant de le téléverser.",
};

export function englishResumeRequiredMessage(lang) {
  return COPY[lang] || COPY.en;
}
